import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError, Slot, SlotGroup, SlotGroupBookingLine, OnDemandService, AppointmentTimesData, BOOKABLE_TYPE_LABELS, seatLimit } from '../api';
import Icon, { slotIcon } from '../design/Icon';
import { Sheet, inr, splitTime, fmtTime, fmtDateShort, fmtDateLong, todayStr, addDaysStr } from '../design/atoms';
import { MemberGym } from '../hooks/useMemberGyms';
import { BookingPay, canBookUnpaid, goToPay, gymCollectsBookings, paidChargeLine, paidCta } from '../bookingPay';
import FrontDeskNote from '../components/FrontDeskNote';

/* SPEC 03 Book (browse) · 04 Slot sheet · 05 Booking confirmed. */

interface OutletCtx {
  activeGymId: string | null;
  activeGym: MemberGym | null;
  hasClassScheduling: boolean;
  loading: boolean;
}

/** What the success screen needs — built from a Slot or an on-demand booking. */
export interface BookingSummary {
  name: string;
  date: string;
  startTime: string;
  durationMinutes: number;
  coach: { id: string; name: string } | null;
  roomName: string | null;
  entitlement: 'included' | 'pack' | 'paid';
  price: number | null;
  /** Consecutive-slot group bookings: end of the last slot → time range on the success screen. */
  endTime?: string;
  /** Overrides the derived "Charged" line (e.g. "1 slot from plan · 1 slot paid"). */
  chargedLabel?: string;
}

/** Extra summary fields a consecutive-slot checkout produces. */
interface GroupBookingExtras {
  endTime: string;
  durationMinutes: number;
  chargedLabel: string;
}

const PERIOD_LABELS = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' } as const;

function minutesBetween(a: string, b: string): number {
  const [ah, am] = a.split(':').map(Number);
  const [bh, bm] = b.split(':').map(Number);
  return (bh * 60 + bm) - (ah * 60 + am);
}

/** "Charged" line for a consecutive-slot checkout — each slot resolves its own entitlement. */
function groupChargedLabel(lines: SlotGroupBookingLine[], unitPrice: number | null, gym: MemberGym | null): string {
  if (lines.length === 0) return '—';
  const counts: Record<'included' | 'pack' | 'paid', number> = { included: 0, pack: 0, paid: 0 };
  for (const l of lines) counts[l.entitlement] += 1;
  const kinds = (['included', 'pack', 'paid'] as const).filter(k => counts[k] > 0);
  if (kinds.length === 1) {
    const n = lines.length;
    if (kinds[0] === 'included') return 'Included in plan';
    if (kinds[0] === 'pack') return n === 1 ? '1 pack credit' : `${n} pack credits`;
    return unitPrice != null ? paidChargeLine(unitPrice * n, gym) : `${n} slots to pay for`;
  }
  return kinds
    .map(k => `${counts[k]} slot${counts[k] === 1 ? '' : 's'} ${k === 'included' ? 'from plan' : k === 'pack' ? 'from pack' : 'to pay for'}`)
    .join(' · ');
}

export default function MemberBookPage() {
  const { token } = useMemberAuth();
  const { activeGymId, activeGym, loading: gymsLoading } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const today = todayStr();
  const dates = useMemo(() => Array.from({ length: 14 }, (_, i) => addDaysStr(today, i)), [today]);

  const [date, setDate] = useState(searchParams.get('date') ?? today);
  const [type, setType] = useState(searchParams.get('type') ?? 'all');
  const [enabledTypes, setEnabledTypes] = useState<string[]>([]);
  const [groups, setGroups] = useState<SlotGroup[]>([]);
  const [services, setServices] = useState<OnDemandService[]>([]);
  const [nextAvailable, setNextAvailable] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Slot | null>(null);
  const [apptService, setApptService] = useState<OnDemandService | null>(null);
  const [confirmed, setConfirmed] = useState<{ summary: BookingSummary; paid: boolean } | null>(null);

  useEffect(() => {
    if (!token || !activeGymId) return;
    mfetch(token, '/member/bookable-config', { params: { gymId: activeGymId } })
      .then(r => setEnabledTypes(r.data.enabledTypes))
      .catch(() => setEnabledTypes([]));
  }, [token, activeGymId]);

  const loadSlots = useCallback(() => {
    if (!token || !activeGymId) return;
    setLoading(true);
    setError(null);
    mfetch(token, '/member/slots', {
      params: { gymId: activeGymId, date, type: type === 'all' ? undefined : type },
    })
      .then(r => {
        setGroups(r.data.groups);
        setServices(r.data.services ?? []);
        setNextAvailable(r.data.nextAvailableDate);
      })
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load sessions'))
      .finally(() => setLoading(false));
  }, [token, activeGymId, date, type]);

  useEffect(loadSlots, [loadSlots]);

  const q = query.trim().toLowerCase();
  const visibleGroups = q
    ? groups
        .map(g => ({ ...g, slots: g.slots.filter(s => `${s.name} ${s.coach?.name ?? ''} ${s.roomName ?? ''}`.toLowerCase().includes(q)) }))
        .filter(g => g.slots.length > 0)
    : groups;
  const visibleServices = q
    ? services.filter(s => `${s.name} ${s.category ?? ''}`.toLowerCase().includes(q))
    : services;
  const firstAvailableId = visibleGroups.flatMap(g => g.slots).find(s => s.spotsLeft > 0 && !s.myBookingId)?.id;
  const isEmpty = !loading && visibleGroups.every(g => g.slots.length === 0);

  const chips = [
    { key: 'all', label: 'All' },
    ...enabledTypes.map(t => ({ key: t, label: BOOKABLE_TYPE_LABELS[t] ?? t })),
  ];

  // Picked slot + up to two available back-to-back follow-ons of the same class.
  // Slots carry no classId, so within one day's list we match by name equality
  // plus startTime === prev.endTime (safe: same class, same day, contiguous).
  const chain = useMemo(() => {
    if (!picked) return [];
    const all = groups.flatMap(g => g.slots);
    const out = [picked];
    const limit = seatLimit(picked, 'slots');
    while (out.length < limit) {
      const prev = out[out.length - 1];
      const next = all.find(c =>
        c.name === picked.name &&
        c.startTime === prev.endTime &&
        c.spotsLeft > 0 &&
        !c.myBookingId &&
        !out.some(x => x.id === c.id)
      );
      if (!next) break;
      out.push(next);
    }
    return out;
  }, [picked, groups]);

  const pickService = (s: OnDemandService) => {
    if (s.scheduleMode === 'grid') {
      navigate(`/app/book/courts/${s.classId}?date=${date}`, { state: { name: s.name } });
    } else {
      setApptService(s);
    }
  };

  if (!gymsLoading && !activeGymId) {
    return (
      <div className="mpage">
        <div className="dp dp-l" style={{ paddingTop: 8 }}>Book</div>
        <div className="card flat"><div className="sub wrap">Join a gym to start booking sessions.</div></div>
      </div>
    );
  }

  return (
    <div className="mpage">
      {/* Header */}
      <div className="between" style={{ paddingTop: 8 }}>
        <div className="dp dp-l">Book</div>
        <div className="row" style={{ gap: 8 }}>
          <button className="icon-btn" aria-label="Coaches" onClick={() => navigate('/app/coaches')}>
            <Icon n="user" s={18} c="var(--ink-2)" />
          </button>
          <button className="icon-btn" aria-label="My bookings" onClick={() => navigate('/app/bookings')}>
            <Icon n="cal" s={18} c="var(--ink-2)" />
          </button>
          <button className="icon-btn" aria-label="Search" onClick={() => { setSearchOpen(v => !v); setQuery(''); }}>
            <Icon n={searchOpen ? 'x' : 'search'} s={18} c="var(--ink-2)" />
          </button>
        </div>
      </div>

      {searchOpen && (
        <input
          className="minput"
          placeholder="Search sessions, coaches, rooms"
          value={query}
          autoFocus
          onChange={e => setQuery(e.target.value)}
        />
      )}

      {/* Category chips */}
      {chips.length > 1 && (
        <div className="hscroll">
          {chips.map(c => (
            <button key={c.key} className={`chip${type === c.key ? ' on' : ''}`} onClick={() => setType(c.key)}>
              {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Date strip */}
      <div className="hscroll">
        {dates.map(d => {
          const on = d === date;
          const dt = new Date(`${d}T00:00:00`);
          return (
            <button
              key={d}
              onClick={() => setDate(d)}
              className="col"
              style={{
                alignItems: 'center', gap: 3, padding: '8px 0', minWidth: 46, flex: '0 0 46px',
                borderRadius: 14,
                background: on ? 'var(--ink)' : 'var(--card)',
                border: `1px solid ${on ? 'var(--ink)' : 'var(--line)'}`,
                color: on ? 'var(--on-ink)' : 'var(--ink)',
              }}
            >
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.1em', opacity: .6 }}>
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][dt.getDay()]}
              </div>
              <div className="num" style={{ fontSize: 17 }}>{dt.getDate()}</div>
            </button>
          );
        })}
      </div>

      {/* Slot list */}
      {loading ? (
        <div className="col" style={{ gap: 9 }}>
          {[64, 64, 64].map((h, i) => <div key={i} className="skel" style={{ height: h }} />)}
        </div>
      ) : error ? (
        <div className="card flat">
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={loadSlots}>Retry</button>
        </div>
      ) : (
        <>
          {isEmpty && <ServicesSection services={visibleServices} onPick={pickService} />}
          {isEmpty ? (
            <div className="card flat" style={{ textAlign: 'center', padding: 26 }}>
              <div className="mid">No sessions on this date</div>
              {nextAvailable && (
                <button className="btn ghost sm" style={{ marginTop: 12 }} onClick={() => setDate(nextAvailable)}>
                  Next available: {fmtDateShort(nextAvailable)}
                </button>
              )}
            </div>
          ) : (
            <div className="col" style={{ gap: 9 }}>
              {visibleGroups.map(g => (
                <div key={g.period} className="col" style={{ gap: 9 }}>
                  <div className="between" style={{ padding: '4px 2px 0' }}>
                    <div className="lbl">{PERIOD_LABELS[g.period]}</div>
                    <div className="tiny">{g.availableCount} available</div>
                  </div>
                  {g.slots.map(s => (
                    <SlotRow key={s.id} s={s} highlight={s.id === firstAvailableId} onPick={() => setPicked(s)} />
                  ))}
                </div>
              ))}
            </div>
          )}
          {!isEmpty && <ServicesSection services={visibleServices} onPick={pickService} />}
        </>
      )}

      {picked && (
        <SlotSheet
          slot={picked}
          chain={chain}
          gymId={activeGymId!}
          gym={activeGym}
          token={token!}
          onClose={() => setPicked(null)}
          onBooked={(paid, extras) => {
            setConfirmed({
              summary: {
                name: picked.name,
                date: picked.date,
                startTime: picked.startTime,
                durationMinutes: extras?.durationMinutes ?? picked.durationMinutes,
                coach: picked.coach,
                roomName: picked.roomName,
                entitlement: picked.entitlement,
                price: picked.price,
                ...(extras ? { endTime: extras.endTime, chargedLabel: extras.chargedLabel } : {}),
              },
              paid,
            });
            setPicked(null);
            loadSlots();
          }}
          onWaitlisted={() => {
            setPicked(null);
            loadSlots();
          }}
        />
      )}

      {apptService && (
        <AppointmentSheet
          service={apptService}
          gymId={activeGymId!}
          gym={activeGym}
          branchId={activeGym?.branches[0]?.id}
          date={date}
          token={token!}
          onClose={() => setApptService(null)}
          onBooked={(summary, paid) => {
            setConfirmed({ summary, paid });
            setApptService(null);
            loadSlots();
          }}
        />
      )}

      {confirmed && (
        <SuccessScreen
          s={confirmed.summary}
          paid={confirmed.paid}
          gym={activeGym}
          onDone={() => setConfirmed(null)}
          onViewBookings={() => navigate('/app/bookings')}
        />
      )}
    </div>
  );
}

// ── On-demand services (appointments / court grid) ──────────────────────────
function ServicesSection({ services, onPick }: { services: OnDemandService[]; onPick: (s: OnDemandService) => void }) {
  if (services.length === 0) return null;
  return (
    <div className="col" style={{ gap: 9 }}>
      <div className="lbl" style={{ padding: '4px 2px 0' }}>Book on demand</div>
      {services.map(s => (
        <button key={s.classId} className="card" style={{ padding: 14, width: '100%', textAlign: 'left' }} onClick={() => onPick(s)}>
          <div className="row">
            <div className="li gold">
              <Icon n={slotIcon(s.bookableType, s.name)} s={17} c="var(--gold-2)" />
            </div>
            <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
              <div className="mid trunc">{s.name}</div>
              <div className="tiny trunc">
                {s.durationMinutes} min · {s.category ?? BOOKABLE_TYPE_LABELS[s.bookableType] ?? s.bookableType}
              </div>
            </div>
            {s.price != null && <span className="tag gold">{inr(s.price)}</span>}
            <Icon n="right" s={16} c="var(--ink-2)" />
          </div>
        </button>
      ))}
    </div>
  );
}

// ── Slot row (COMPONENTS.md "Slot row") ─────────────────────────────────────
function SlotRow({ s, highlight, onPick }: { s: Slot; highlight: boolean; onPick: () => void }) {
  const { time, ampm } = splitTime(s.startTime);
  const soldOut = s.spotsLeft <= 0 && !s.myBookingId;
  const rate =
    s.entitlement === 'included' ? <span className="tag sage">Included</span> :
    s.entitlement === 'pack' ? <span className="tag gold">{s.packRemaining} left in pack</span> :
    <span className="tag gold">{s.price != null ? inr(s.price) : 'Paid'}</span>;

  return (
    <button className={`slot${soldOut ? ' full' : ''}${highlight ? ' hl' : ''}`} onClick={onPick}>
      <div className="t">
        <b>{time}</b>
        <span>{ampm}</span>
      </div>
      <div className="col" style={{ gap: 3, flex: 1 }}>
        <div className="mid">{s.name}</div>
        <div className="tiny">{[s.coach?.name, s.roomName ?? s.branchName].filter(Boolean).join(' · ')}</div>
        <div className="row" style={{ gap: 6, marginTop: 2 }}>
          {s.myBookingId ? <span className="tag sage">Booked</span> : rate}
          <span className="tiny">
            {s.myBookingId ? '' : soldOut ? 'Waitlist' : `${s.spotsLeft} spot${s.spotsLeft === 1 ? '' : 's'} left`}
          </span>
        </div>
      </div>
      <div className="go">
        <Icon n={soldOut ? 'clock' : 'right'} s={16} c={highlight ? 'var(--on-ink)' : 'var(--ink-2)'} />
      </div>
    </button>
  );
}

// ── Slot sheet (SPEC 04) ─────────────────────────────────────────────────────
function SlotSheet({
  slot, chain, gymId, gym, token, onClose, onBooked, onWaitlisted,
}: {
  slot: Slot;
  /** slot + available back-to-back follow-ons of the same class (length 1-3). */
  chain: Slot[];
  gymId: string;
  gym: MemberGym | null;
  token: string;
  onClose: () => void;
  onBooked: (paid: boolean, extras?: GroupBookingExtras) => void;
  onWaitlisted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ name: string; startTime: string } | null>(null);
  const [waitlistPos, setWaitlistPos] = useState<number | null>(null);
  const [count, setCount] = useState(1); // duration multiplier (1-3 consecutive slots)

  const soldOut = slot.spotsLeft <= 0 && !slot.myBookingId;
  const paid = slot.entitlement === 'paid' && slot.price != null;
  // A paid drop-in the gym won't let a member owe for, at a gym that can't
  // collect online, belongs to the desk — the server would refuse it anyway.
  const deskOnly = slot.entitlement === 'paid' && !canBookUnpaid(slot.allowUnpaidBooking, gym);
  const canExtend = chain.length > 1 && !soldOut && !slot.myBookingId && !slot.onWaitlist;
  const selected = canExtend ? chain.slice(0, count) : [slot];

  const confirm = async () => {
    setBusy(true);
    setError(null);
    setConflict(null);
    const started = Date.now();
    try {
      if (selected.length > 1) {
        const r = await mfetch(token, '/member/bookings', {
          method: 'POST',
          body: { gymId, slotIds: selected.map(s => s.id) },
        });
        // Where the gym collects, the seats are held while the member pays.
        const pay: BookingPay | null = r.data?.pay ?? null;
        if (pay) return goToPay(pay.url);
        const lines: SlotGroupBookingLine[] = r.data?.bookings ?? [];
        const endTime = lines[lines.length - 1]?.endTime ?? selected[selected.length - 1].endTime;
        const extras: GroupBookingExtras = {
          endTime,
          durationMinutes: minutesBetween(slot.startTime, endTime),
          chargedLabel: groupChargedLabel(lines, slot.price, gym),
        };
        const anyPaid = lines.some(l => l.entitlement === 'paid');
        // Keep a 400ms minimum so the busy state reads as deliberate (MOTION.md)
        const wait = Math.max(0, 400 - (Date.now() - started));
        setTimeout(() => onBooked(anyPaid, extras), wait);
        return;
      }
      const r = await mfetch(token, '/member/bookings', { method: 'POST', body: { gymId, slotId: slot.id } });
      const pay: BookingPay | null = r.data?.pay ?? null;
      if (pay) return goToPay(pay.url);
      // Keep a 400ms minimum so the busy state reads as deliberate (MOTION.md)
      const wait = Math.max(0, 400 - (Date.now() - started));
      setTimeout(() => onBooked(paid), wait);
    } catch (e) {
      setBusy(false);
      if (e instanceof MemberApiError) {
        if (e.code === 'BOOKING_CONFLICT' && e.body?.conflicting) {
          setConflict({ name: e.body.conflicting.name, startTime: e.body.conflicting.startTime });
        } else {
          setError(e.message);
        }
      } else {
        setError('Could not complete the booking. Please try again.');
      }
    }
  };

  const joinWaitlist = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await mfetch(token, `/class-sessions/${slot.id}/waitlist`, { method: 'POST', body: { gymId } });
      setWaitlistPos(r.data?.position ?? null);
      setBusy(false);
    } catch (e) {
      setBusy(false);
      setError(e instanceof MemberApiError ? e.message : 'Could not join the waitlist.');
    }
  };

  return (
    <Sheet onClose={busy ? () => {} : onClose}>
      <div className="between">
        <div className="row" style={{ gap: 6 }}>
          <span className="tag">{BOOKABLE_TYPE_LABELS[slot.bookableType] ?? slot.bookableType}</span>
          <span className="tag">{slot.durationMinutes * selected.length} min</span>
        </div>
        <button className="icon-btn" aria-label="Close" onClick={onClose}>
          <Icon n="x" s={16} c="var(--ink-2)" />
        </button>
      </div>

      <div className="col" style={{ gap: 4 }}>
        <div className="dp dp-m" style={{ whiteSpace: 'normal' }}>{slot.name}</div>
        <div className="sub">
          {fmtDateShort(slot.date)} · {fmtTime(slot.startTime)}{slot.roomName ? ` · ${slot.roomName}` : ''}
        </div>
      </div>

      {slot.coach && (
        <div className="card flat" style={{ padding: '10px 12px' }}>
          <div className="row">
            <div className="avatar">{slot.coach.name.charAt(0).toUpperCase()}</div>
            <div className="col" style={{ gap: 1, minWidth: 0 }}>
              <div className="mid trunc">{slot.coach.name}</div>
              <div className="tiny">Coach</div>
            </div>
          </div>
        </div>
      )}

      {canExtend && (
        <div className="col" style={{ gap: 8 }}>
          <div className="lbl">Duration</div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {chain.map((_, i) => i + 1).map(n => (
              <button
                key={n}
                className={`chip${count === n ? ' on' : ''}`}
                onClick={() => setCount(n)}
              >
                {slot.durationMinutes * n} min
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid2">
        <div className="field">
          <span className="lbl">Spots left</span>
          <b className="num">
            {Math.max(0, Math.min(...selected.map(s => s.spotsLeft)))} of {Math.min(...selected.map(s => s.capacity))}
          </b>
        </div>
        <div className="field">
          <span className="lbl">Your rate</span>
          <b>
            {slot.entitlement === 'included' ? 'Included' :
             slot.entitlement === 'pack' ? `${slot.packRemaining} left in pack` :
             slot.price != null ? (
               selected.length > 1
                 ? <>{inr(slot.price * selected.length)}{' '}<span className="tiny">{selected.length} × {inr(slot.price)}</span></>
                 : inr(slot.price)
             ) : 'Paid'}
          </b>
        </div>
      </div>

      <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
        <Icon n="shield" s={15} c="var(--ink-3)" />
        <span className="tiny wrap" style={{ lineHeight: 1.4 }}>
          Free cancellation until {slot.cancellationCutoffHours} {slot.cancellationCutoffHours === 1 ? 'hr' : 'hrs'} before the session.
          {slot.entitlement === 'pack' ? ' Late cancels use one credit.' : ''}
        </span>
      </div>

      {conflict && (
        <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
          <span className="sub wrap" style={{ color: 'var(--clay)' }}>
            You already have {conflict.name} at {fmtTime(conflict.startTime)} that day.
          </span>
        </div>
      )}
      {error && (
        <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
          <span className="sub wrap" style={{ color: 'var(--clay)' }}>{error}</span>
        </div>
      )}
      {waitlistPos !== null && (
        <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--gold-line)', background: 'var(--gold-soft)' }}>
          <span className="sub wrap" style={{ color: 'var(--gold-2)' }}>
            You're on the waitlist{waitlistPos ? ` — position ${waitlistPos}` : ''}. We'll let you know if a spot opens.
          </span>
        </div>
      )}

      {waitlistPos !== null ? (
        <button className="btn block" onClick={onWaitlisted}>Done</button>
      ) : slot.myBookingId ? (
        <button className="btn block" onClick={onClose}>Already booked</button>
      ) : slot.onWaitlist ? (
        <button className="btn block" disabled>On the waitlist</button>
      ) : soldOut ? (
        <button className="btn block" disabled={busy} onClick={joinWaitlist}>
          {busy ? 'Joining…' : 'Join waitlist'}
        </button>
      ) : deskOnly ? (
        <FrontDeskNote gym={gym} />
      ) : (
        <button className="btn gold block" disabled={busy || !!conflict} onClick={confirm}>
          {busy ? (gymCollectsBookings(gym) && paid ? 'Opening payment…' : 'Confirming…')
            : paid ? paidCta(slot.price! * selected.length, gym) : 'Confirm booking'}
        </button>
      )}
    </Sheet>
  );
}

// ── Appointment sheet (on-demand, computed start times) ──────────────────────
function AppointmentSheet({
  service, gymId, gym, branchId, date, token, onClose, onBooked,
}: {
  service: OnDemandService;
  gymId: string;
  gym: MemberGym | null;
  branchId: string | undefined;
  date: string;
  token: string;
  onClose: () => void;
  onBooked: (summary: BookingSummary, paid: boolean) => void;
}) {
  const [data, setData] = useState<AppointmentTimesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [staffId, setStaffId] = useState<string | null>(null); // null = Anyone
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitlistId, setWaitlistId] = useState<string | null>(null); // "notify me" entry for this day
  const [wlBusy, setWlBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    mfetch(token, '/member/appointment-times', {
      params: { gymId, classId: service.classId, date, branchId },
    })
      .then(r => {
        setData(r.data);
        setWaitlistId(r.data.waitlistEntryId ?? null);
      })
      .catch(e => setLoadError(e instanceof MemberApiError ? e.message : 'Could not load available times'))
      .finally(() => setLoading(false));
  }, [token, gymId, service.classId, date, branchId]);

  const joinDayWaitlist = async () => {
    setWlBusy(true);
    setError(null);
    try {
      const r = await mfetch(token, '/member/appointment-waitlist', {
        method: 'POST',
        body: { gymId, classId: service.classId, date },
      });
      setWaitlistId(r.data.id);
    } catch (e) {
      setError(e instanceof MemberApiError ? e.message : 'Could not set up the notification.');
    } finally {
      setWlBusy(false);
    }
  };

  const undoDayWaitlist = async () => {
    if (!waitlistId) return;
    setWlBusy(true);
    setError(null);
    try {
      await mfetch(token, `/member/appointment-waitlist/${waitlistId}`, { method: 'DELETE' });
      setWaitlistId(null);
    } catch (e) {
      setError(e instanceof MemberApiError ? e.message : 'Could not remove the notification.');
    } finally {
      setWlBusy(false);
    }
  };

  useEffect(load, [load]);

  const times = data ? (staffId ? data.times.filter(t => t.staffIds.includes(staffId)) : data.times) : [];
  // Only 'member' policy lets the member pick a provider; auto/manual hide names.
  const showStaffPicker = !!data && data.assignmentPolicy === 'member' && data.staff.length > 0;
  const paid = data?.entitlement === 'paid' && data.price != null;
  const deskOnly = data?.entitlement === 'paid' && !canBookUnpaid(data.allowUnpaidBooking, gym);

  const pickStaff = (id: string | null) => {
    setStaffId(id);
    if (id && time) {
      const entry = data?.times.find(t => t.startTime === time);
      if (!entry || !entry.staffIds.includes(id)) setTime(null);
    }
  };

  const confirm = async () => {
    if (!data || !time) return;
    setBusy(true);
    setError(null);
    const started = Date.now();
    try {
      const r = await mfetch(token, '/member/bookings', {
        method: 'POST',
        body: {
          gymId,
          classId: service.classId,
          branchId: data.branchId,
          date,
          startTime: time,
          ...(staffId ? { staffId } : {}),
        },
      });
      const pay: BookingPay | null = r.data?.pay ?? null;
      if (pay) return goToPay(pay.url);
      const assignedId: string | null = r.data?.session?.staffId ?? staffId;
      const coach = assignedId ? data.staff.find(st => st.id === assignedId) ?? null : null;
      const summary: BookingSummary = {
        name: service.name,
        date,
        startTime: r.data?.session?.startTime ?? time,
        durationMinutes: data.durationMinutes,
        coach: coach ? { id: coach.id, name: coach.name } : null,
        roomName: null,
        entitlement: data.entitlement,
        price: data.price,
      };
      // Keep a 400ms minimum so the busy state reads as deliberate (MOTION.md)
      const wait = Math.max(0, 400 - (Date.now() - started));
      setTimeout(() => onBooked(summary, paid), wait);
    } catch (e) {
      setBusy(false);
      if (e instanceof MemberApiError) {
        if (e.code === 'BOOKING_CONFLICT' && e.body?.conflicting) {
          setError(`You already have ${e.body.conflicting.name} at ${fmtTime(e.body.conflicting.startTime)} that day.`);
        } else {
          setError(e.message);
        }
      } else {
        setError('Could not complete the booking. Please try again.');
      }
    }
  };

  return (
    <Sheet onClose={busy ? () => {} : onClose}>
      <div className="between">
        <div className="row" style={{ gap: 6 }}>
          <span className="tag">{BOOKABLE_TYPE_LABELS[service.bookableType] ?? service.bookableType}</span>
          <span className="tag">{service.durationMinutes} min</span>
        </div>
        <button className="icon-btn" aria-label="Close" onClick={onClose}>
          <Icon n="x" s={16} c="var(--ink-2)" />
        </button>
      </div>

      <div className="col" style={{ gap: 4 }}>
        <div className="dp dp-m" style={{ whiteSpace: 'normal' }}>{service.name}</div>
        <div className="sub">{fmtDateShort(date)}</div>
      </div>

      {loading ? (
        <div className="col" style={{ gap: 9 }}>
          {[36, 36, 52].map((h, i) => <div key={i} className="skel" style={{ height: h }} />)}
        </div>
      ) : loadError ? (
        <div className="card flat">
          <div className="sub wrap">{loadError}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
        </div>
      ) : data && (
        <>
          {showStaffPicker && (
            <div className="col" style={{ gap: 8 }}>
              <div className="lbl">With</div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <button className={`chip${staffId === null ? ' on' : ''}`} onClick={() => pickStaff(null)}>Anyone</button>
                {data.staff.map(st => (
                  <button key={st.id} className={`chip${staffId === st.id ? ' on' : ''}`} onClick={() => pickStaff(st.id)}>
                    {st.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {times.length === 0 ? (
            <div className="card flat" style={{ textAlign: 'center', padding: 22 }}>
              <div className="mid">No times available on this date</div>
              <div className="tiny wrap" style={{ marginTop: 4 }}>Try another date from the strip above.</div>
              {data.times.length === 0 && (
                waitlistId === null ? (
                  <button className="btn ghost block" style={{ marginTop: 14 }} disabled={wlBusy} onClick={joinDayWaitlist}>
                    {wlBusy ? 'One moment…' : 'Notify me if a time opens'}
                  </button>
                ) : (
                  <div className="row" style={{ gap: 10, justifyContent: 'center', marginTop: 14, flexWrap: 'wrap' }}>
                    <span className="tiny wrap" style={{ color: 'var(--sage)' }}>
                      We'll message you if a booking on this day frees up
                    </span>
                    <button className="btn ghost sm" disabled={wlBusy} onClick={undoDayWaitlist}>Undo</button>
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {times.map(t => (
                <button
                  key={t.startTime}
                  className={`chip${time === t.startTime ? ' on' : ''}`}
                  onClick={() => setTime(t.startTime)}
                >
                  {fmtTime(t.startTime)}
                </button>
              ))}
            </div>
          )}

          <div className="field">
            <span className="lbl">Your rate</span>
            <b>
              {data.entitlement === 'included' ? 'Included' :
               data.entitlement === 'pack' ? `${data.packRemaining} left in pack` :
               data.price != null ? inr(data.price) : 'Paid'}
            </b>
          </div>

          <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <Icon n="shield" s={15} c="var(--ink-3)" />
            <span className="tiny wrap" style={{ lineHeight: 1.4 }}>
              Free cancellation until {data.cancellationCutoffHours} {data.cancellationCutoffHours === 1 ? 'hr' : 'hrs'} before the session.
              {data.entitlement === 'pack' ? ' Late cancels use one credit.' : ''}
            </span>
          </div>

          {error && (
            <div className="card flat" style={{ padding: '10px 12px', borderColor: 'var(--clay)', background: 'var(--clay-soft)' }}>
              <span className="sub wrap" style={{ color: 'var(--clay)' }}>{error}</span>
            </div>
          )}

          {deskOnly ? (
            <FrontDeskNote gym={gym} />
          ) : (
            <button className="btn gold block" disabled={busy || !time} onClick={confirm}>
              {busy ? (gymCollectsBookings(gym) && paid ? 'Opening payment…' : 'Confirming…')
                : paid ? paidCta(data.price!, gym) : 'Confirm booking'}
            </button>
          )}
        </>
      )}
    </Sheet>
  );
}

// ── Success screen (SPEC 05) ─────────────────────────────────────────────────
function SuccessScreen({
  s, paid, gym, onDone, onViewBookings,
}: {
  s: BookingSummary;
  paid: boolean;
  gym: MemberGym | null;
  onDone: () => void;
  onViewBookings: () => void;
}) {
  return (
    <div className="ok-wrap">
      <div className="ok">
        <i /><i />
        <b>
          <svg width={38} height={38} viewBox="0 0 24 24" fill="none" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
        </b>
      </div>
      <div className="col rise" style={{ alignItems: 'center', gap: 6, textAlign: 'center' }}>
        <div className="lbl-d">You're booked</div>
        <div className="dp dp-xl" style={{ whiteSpace: 'normal' }}>{s.name}</div>
        <div className="sub">
          {fmtDateLong(s.date)} · {fmtTime(s.startTime)}{s.endTime ? ` – ${fmtTime(s.endTime)}` : ''}
        </div>
      </div>
      <div className="card rise-2" style={{ width: '100%', maxWidth: 340, marginTop: 22 }}>
        {[
          s.coach && ['Coach', s.coach.name],
          s.roomName && ['Where', s.roomName],
          ['Duration', `${s.durationMinutes} min`],
          // Only where nothing was taken online does this screen show at all
          // for a paid booking (a collecting gym goes to the pay page), so a
          // price here is owed at the desk — never "charged".
          [paid ? 'Payment' : 'Charged', s.chargedLabel ?? (s.entitlement === 'included' ? 'Included in plan' : s.entitlement === 'pack' ? '1 pack credit' : paid && s.price != null ? paidChargeLine(s.price, gym) : '—')],
        ]
          .filter(Boolean)
          .map(pair => {
            const [k, v] = pair as [string, string];
            return (
              <div key={k} className="lrow">
                <span className="lbl" style={{ width: 74 }}>{k}</span>
                <span className="mid trunc" style={{ flex: 1, textAlign: 'right' }}>{v}</span>
              </div>
            );
          })}
      </div>
      <div className="col rise-2" style={{ width: '100%', maxWidth: 340, gap: 10, marginTop: 18 }}>
        <button className="btn gold block" onClick={onViewBookings}>View my bookings</button>
        <button className="btn ghost block" onClick={onDone}>Done</button>
      </div>
    </div>
  );
}
